import {
  AfterViewInit, ChangeDetectorRef,
  Component, DoCheck,
  ElementRef, EventEmitter,
  Host,
  Input,
  OnDestroy,
  Optional, Output,
  SkipSelf,
  ViewChild
} from '@angular/core';
import {AbstractControl, ControlContainer, ControlValueAccessor, Form, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Subscription} from 'rxjs/Subscription';
import {coerceBooleanProperty, coerceNumberProperty,} from '@angular/cdk/coercion';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {FormSelectMultipleItem} from '@common/form-components';
import {fromEvent} from 'rxjs/observable/fromEvent';
import {Subject} from 'rxjs/Subject';

@Component({
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FormSelectSingleComponent,
    }
  ],
  selector: 'app-form-select-single',
  templateUrl: './form-select-single.component.html',
  styleUrls: ['./form-select-single.component.scss', '../form-components-styles.scss']
})
export class FormSelectSingleComponent implements AfterViewInit, OnDestroy, ControlValueAccessor, DoCheck {

  // Label del Input
  @Input() label = '';

  @Input() labelSize: 'sm' | 'md' = 'sm';

  // Mantener el espacio del label, desactivar para casos donde no se requiere label
  _keepLabelSpace = true;
  @Input() set keepLabelSpace(val: boolean) {
    this._keepLabelSpace = coerceBooleanProperty(val);
  }

  // Placeholder del Select
  @Input() placeholder = '';

  // Mensaje de error Inferior (solo acepta uno, podria prepararse una property para un array y mostrar en vertical)
  @Input() errorMessage = '';

  // Mantener el espacio del error, desactivar si no hay validaciones (mantener en formularios para evitar conflictos de tamaño de inputs)
  _keepErrorSpace = true;
  @Input() set keepErrorSpace(val: boolean) {
    this._keepErrorSpace = coerceBooleanProperty(val);
  }

  // Icono de google (fonts.google.com/icons)
  @Input() googleIconName = '';

  // Icono SVG local
  @Input() svgIconName = '';

  _menuOptions: FormSelectItem[] = [];
  @Input() set menuOptions(val: FormSelectItem[]) {
    this._menuOptions = [...val];
    this._allOptions = [...val];
  }

  // usamos esta variable como backup
  _allOptions: FormSelectItem[] = [];

  // Mostrar el boton X para eliminar
  _allowClearButton = false;
  @Input() set allowClearButton(val: boolean) {
    this._allowClearButton = coerceBooleanProperty(val);
  }

  // Mostrar un spinner en lugar del icono (se recomienda deshabilitar el componente)
  _loading = false;
  @Input() set loading(val: boolean) {
    this._loading = coerceBooleanProperty(val);
  }

  // Flag menu abierto
  overlayIsOpen = false;

  // offset Y
  offsetY = 5;

  @ViewChild('controlContainer')
  private controlContainerElement: ElementRef;

  // Evento de inicializacion en caso de usar NGIF
  @Output() initialized = new EventEmitter();
  // Subscripcion para la searchbar, setear la variable para que se desuscriba al eliminar el componente
  public searchSubscription = new Subscription();

  // tooltip en hover
  @Input() tooltip = '';
  // posiicon del tooltip
  @Input() tooltipPosition: 'left' | 'right' | 'above' | 'below' | 'before' | 'after' = 'above';
  // clase generica del tooltip (para añadir en styles.scss)
  @Input() tooltipClass = '';

  // Atributos de Form

  // Valor del Selector
  value: FormSelectItem | null = null;

  // Flag Touched
  touched = false;

  // Flag Disabled
  disabled = false;

  // Flag Control Valido
  validStatus = false;

  // Hacky, levantamos el control usando el mismo nombre de property que se usa para acceder al form
  @Input() formControlName: string;
  // Control del Form
  private control: AbstractControl;
  // Subscripcion al estado Valid
  private statusSubscription: Subscription = new Subscription();

  // valor de la barra de busqueda
  searchValue = '';
  searchValueSubject = new BehaviorSubject<string>('');
  $searchValue = this.searchValueSubject.asObservable();

  _integratedSearch = true;
  // Definimos si realizamos una busqueda dentro del componente usando un criterio de comparacion de string con el Label
  // en caso de requerir una busqueda mas avanzada se debe realizar de forma externa
  @Input() set integratedSearch(val: boolean) {
    this._integratedSearch = coerceBooleanProperty(val);
  }

  _allowSearchMinimum = 5;
  // Minimo de opciones para permitir la barra de busqueda. Default 5, usar 0 para tener search siempre
  @Input() set allowSearchMinimum(val: number) {
    this._allowSearchMinimum = coerceNumberProperty(val);
  }

  _showNoValuesAlways = true;
  // Definimos si mostrar el mensaje de no hay valores sirempre o solo cuando hay una busqueda. Util en busquedas externas
  @Input() set showNoValuesAlways(val: boolean) {
    this._showNoValuesAlways = coerceBooleanProperty(val);
  }

  _searchLoading = false;
  // Spinner de carga en la barra de busqueda. Util en busquedas externas
  @Input() set searchLoading(val: boolean) {
    this._searchLoading = coerceBooleanProperty(val);
  }

  @Input() searchPlaceholder = '';

  // Callback al cambiar el valor del input
  onChange = (val) => {};

  // Callback al cambiar a estado Touched
  onTouched = () => {};

  // En el constructor obtenemos el formGroup al cual pertenece el input
  destroySubject = new Subject<void>();
  constructor(
    @Optional() @Host() @SkipSelf()
    private controlContainer: ControlContainer,
    private detectionRef: ChangeDetectorRef,
  ) {
    // dado que al hacer un resize en la ventana no se ejecuta el detection changes, lo ejecutamos manualmente
    fromEvent(window, 'resize').takeUntil(this.destroySubject).subscribe(x => {
      this.forceDetectChanges();
    })
  }

  ngAfterViewInit() {
    this.initializeFormControl();
    window.setTimeout(() => {
      this.initialized.emit();
    });
  }

  ngOnDestroy() {
    this.statusSubscription.unsubscribe();
    this.searchSubscription.unsubscribe();
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  ngDoCheck() {
    if (this.control && this.control.touched && !this.touched) {
      this.markAsTouched();
    }
  }

  // Ejecutamos una busqueda interna
  executeIntegratedSearch() {
    // si encontramos un valor de busqueda, realizamos la busqueda pertinente
    if (this.searchValue.trim().length) {
      this._menuOptions = this._allOptions.filter(x => x.Label.toLowerCase().trim().includes(this.searchValue.toLowerCase().trim()))
    } else {
      this._menuOptions = [...this._allOptions];
    }
  }

  forceDetectChanges() {
    // en caso de que el ciclo de detection changes no se ejecute, podemos llamar a esta funcion
    this.detectionRef.detectChanges();
  }

  initializeFormControl() {
    if (!this.controlContainer) {
      console.warn('Can\'t find parent FormGroup directive');
      return;
    }

    if (!this.formControlName) {
      console.warn('Missing FormControlName directive from host element of the component');
      return;
    }

    this.control = this.controlContainer.control.get(this.formControlName);
    this.validStatus = this.control.valid;
    this.statusSubscription = this.control.statusChanges.subscribe(x => {
      this.validStatus = !this.control.invalid; // usamos invalid pues valid posee un bug
    });
  }

  openMenu() {
    if (!this.disabled) {
      this.overlayIsOpen = true;
    }
  }

  closeMenu() {
    this.overlayIsOpen = false;
    this.markAsTouched();
  }

  // cuando hay un cambio en el select, llamamos a la callbackFunction onchange para que lo registre el form
  selectItem(item: FormSelectItem) {
    if (this.value !== item) {
      this.value = item;
      this.onChange(this.value);
    }
    this.closeMenu();
  }

  // Eliminar la seleccion
  clearValue() {
    if (this.disabled) {
      return;
    }
    this.value = null;
    this.onChange(this.value);
  }

  onSearchChanged() {
    // validamos si tenemos un search interno y seleccionamos solo uno para realizar el search solo una vez
    if (this._integratedSearch) {
      this.executeIntegratedSearch();
    } else {
      this.searchValueSubject.next(this.searchValue);
    }
    // disparamos el evento por si el sistema de overlay no acomoda el panel y necesita mas espacio
    window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 10);
  }

  /** En caso de que se quiera modificar el valor por codigo
   * mediante AbstractControl.setValue(value) se llamará a este método
   * */
  writeValue(val: FormSelectItem) {
    this.value = val;
  }

  /** El formulario registrara esta callbackFunction para notificar cuando hubo un
   * cambio en el formulario del lado del usuario
   * */
  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  /** El formulario registrara esta callbackFunction para notificar el estado Touched
   * */
  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  /** El formulario podra usar esta funcion para deshabilitar el control
   * */
  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  // Marcamos el control como Touched
  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

}

export interface FormSelectItem {
  Id: string | number;
  Label: string;
  RawValue?: string;
  ExtraData?: string;
}



// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/components/common/form-components/form-select-single/form-select-single.component.ts